const MAX_NOTIFICATIONS = 50;

const pick = (row, ...keys) => {
  for (const key of keys) {
    if (row?.[key] != null && row[key] !== '') return row[key];
  }
  return undefined;
};

export const extractNotificationList = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload.filter(Boolean);
  if (Array.isArray(payload.result)) return payload.result.filter(Boolean);
  if (Array.isArray(payload.data)) return payload.data.filter(Boolean);
  if (Array.isArray(payload.notifications)) return payload.notifications.filter(Boolean);
  if (Array.isArray(payload.items)) return payload.items.filter(Boolean);
  if (Array.isArray(payload.records)) return payload.records.filter(Boolean);
  return [];
};

export const notificationIdentity = (n = {}) => {
  const id = pick(n, 'notificationId', 'NotificationId', 'id');
  const key = pick(n, 'notificationKey', 'NotificationKey');
  if (id != null && String(id).trim() !== '') return `id:${id}`;
  if (key != null && String(key).trim() !== '') return `key:${key}`;
  const created = pick(n, 'createdDateTime', 'CreatedDateTime') || '';
  const title = pick(n, 'title', 'Title') || '';
  return `fallback:${created}|${title}`;
};

export const normalizeNotification = (row = {}, index = 0) => {
  const notificationId = pick(row, 'notificationId', 'NotificationId', 'id') ?? null;
  const notificationKey =
    pick(row, 'notificationKey', 'NotificationKey') ??
    (notificationId != null ? String(notificationId) : `local-${index}`);
  const createdDateTime =
    pick(row, 'createdDateTime', 'CreatedDateTime', 'createdAt', 'CreatedAt') || null;
  const type = String(pick(row, 'type', 'Type', 'actionType', 'ActionType') || 'General');
  const isReadRaw = pick(row, 'isRead', 'IsRead');
  const isRead = isReadRaw === true || isReadRaw === 1 || String(isReadRaw).toLowerCase() === 'true';

  return {
    notificationId: notificationId != null ? Number(notificationId) || notificationId : null,
    notificationKey: String(notificationKey),
    affiliateUserId: pick(row, 'affiliateUserId', 'AffiliateUserId', 'userId', 'UserId') ?? null,
    type,
    actionType: type,
    title: String(pick(row, 'title', 'Title', 'notificationTitle', 'NotificationTitle') || 'Notification'),
    message: String(
      pick(row, 'message', 'Message', 'notificationMessage', 'NotificationMessage') || ''
    ),
    createdDateTime,
    deliveredViaSignalR: Boolean(
      pick(row, 'deliveredViaSignalR', 'DeliveredViaSignalR')
    ),
    isRead,
    identity: notificationIdentity({
      notificationId,
      notificationKey,
      createdDateTime,
      title: pick(row, 'title', 'Title'),
    }),
    raw: row,
  };
};

export const normalizeNotificationList = (payload) =>
  extractNotificationList(payload).map((row, index) => normalizeNotification(row, index));

export const sortNotificationsNewestFirst = (list = []) =>
  [...list].sort((a, b) => {
    const ta = new Date(a.createdDateTime || 0).getTime();
    const tb = new Date(b.createdDateTime || 0).getTime();
    return (Number.isFinite(tb) ? tb : 0) - (Number.isFinite(ta) ? ta : 0);
  });

export const mergeNotifications = (existing = [], incoming = [], max = MAX_NOTIFICATIONS) => {
  const map = new Map();
  [...existing, ...incoming].forEach((item) => {
    if (!item) return;
    const key = item.identity || notificationIdentity(item);
    if (!map.has(key)) {
      map.set(key, item);
    } else {
      const prev = map.get(key);
      map.set(key, {
        ...prev,
        ...item,
        isRead: Boolean(prev.isRead && item.isRead),
      });
    }
  });
  return sortNotificationsNewestFirst([...map.values()]).slice(0, max);
};

export const countUnread = (list = []) => list.filter((n) => !n.isRead).length;

export const formatNotificationTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '';
  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

export const MAX_AFFILIATE_NOTIFICATIONS = MAX_NOTIFICATIONS;
